import { Formik } from 'formik'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  ContractorPortalFetchPartners,
  ContractorPortalSignInPhone,
  SignInPhone,
} from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { SignInFormValues } from './SignInForm.interface'
import { SignInSchema } from './SignInForm.validator'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import tw from 'twin.macro'
import parsePhoneNumber from 'libphonenumber-js'
import { TOS } from 'src/components/shared/TOS'
import { handleApiError } from 'src/utils/errorHandlers'
import { Button } from 'src/components/shared/Button'

interface Props {
  isContinue?: boolean
  partnerName?: string
  platform: 'client' | 'contractorPortal'
  onSuccess(phone: string): void
}

const initialValues: SignInFormValues = {
  phone: '',
}

const PHONE_MASK = ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]

export const SignInForm = ({ isContinue, partnerName, platform, onSuccess }: Props) => {
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const handleFormSubmit = async (values: SignInFormValues) => {
    setIsLoading(true)
    setError(null)

    try {
      const parsedPhone = parsePhoneNumber(values.phone, 'US')

      const phone = parsedPhone?.formatInternational().replace(/\s+/g, '') || values.phone

      if (platform === 'client') {
        await createAsyncAction(dispatch, SignInPhone.request({ phone, partnerName }))
      }

      if (platform === 'contractorPortal') {
        const partners: { name: string }[] = await createAsyncAction(
          dispatch,
          ContractorPortalFetchPartners.request({ phone }),
        )

        if (partners.length === 1) {
          await createAsyncAction(
            dispatch,
            ContractorPortalSignInPhone.request({ phone, partnerName: partners[0].name }),
          )
        }
      }

      onSuccess(phone)
    } catch (err: any) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validationSchema={SignInSchema}
    >
      {({ handleSubmit }) => (
        <div>
          <FormikTextField
            name="phone"
            css={tw`w-full mb-6`}
            mask={PHONE_MASK}
            placeholder="Enter your phone number"
          />
          <TOS css={tw`mb-6`} />
          <FormErrorMessage css={tw`mb-4`} error={error} />
          <Button className={tw`w-full`} loading={isLoading} onClick={() => handleSubmit()}>
            {isContinue ? 'Continue' : 'Start'}
          </Button>
        </div>
      )}
    </Formik>
  )
}
